/**
 * Product fitment matcher — metafield first, legacy tag fallback (WIL-205).
 * Reads the same selectedVehicle store as my-garage.js / vehicle selector.
 */
(() => {
  /** @param {string} value */
  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  /** @param {string[]} tags */
  function parseFitmentTags(tags) {
    /** @type {{ makes: string[]; models: string[]; years: string[]; series: string[] }} */
    const fitment = { makes: [], models: [], years: [], series: [] };
    if (!Array.isArray(tags)) return fitment;

    tags.forEach((tag) => {
      if (typeof tag !== 'string') return;
      if (tag.startsWith('make_')) fitment.makes.push(tag.slice(5));
      else if (tag.startsWith('model_')) fitment.models.push(tag.slice(6));
      else if (tag.startsWith('year_')) fitment.years.push(tag.slice(5));
      else if (tag.startsWith('series_')) fitment.series.push(tag.slice(7));
    });

    return fitment;
  }

  /**
   * @param {unknown} raw
   * @returns {Record<string, unknown>[]}
   */
  function parseCompatibleVehicles(raw) {
    if (!raw) return [];

    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed.filter((entry) => entry && typeof entry === 'object');
    }

    if (parsed && typeof parsed === 'object' && Array.isArray(/** @type {{ vehicles?: unknown[] }} */ (parsed).vehicles)) {
      return /** @type {Record<string, unknown>[]} */ (
        /** @type {{ vehicles: unknown[] }} */ (parsed).vehicles.filter((entry) => entry && typeof entry === 'object')
      );
    }

    return [];
  }

  /** @param {unknown} value */
  function parseYear(value) {
    if (value == null || value === '') return null;
    const year = Number.parseInt(String(value), 10);
    return Number.isFinite(year) ? year : null;
  }

  /**
   * @param {string | undefined} selectedSeries
   * @param {unknown} rowSeries
   */
  function seriesMatches(selectedSeries, rowSeries) {
    if (!rowSeries) return true;
    if (!selectedSeries) return true;

    const selected = normalize(selectedSeries);
    const candidate = normalize(String(rowSeries));
    if (!candidate) return true;
    return candidate === selected || candidate.includes(selected) || selected.includes(candidate);
  }

  /**
   * @param {Record<string, string | undefined> | null} vehicle
   * @param {Record<string, unknown>} row
   */
  function vehicleMatchesMetafieldRow(vehicle, row) {
    if (!vehicle?.make) return false;

    if (row.make && normalize(String(row.make)) !== normalize(vehicle.make)) return false;
    if (row.model && normalize(String(row.model)) !== normalize(vehicle.model)) return false;
    if (!seriesMatches(vehicle.series, row.series)) return false;

    const selectedYear = parseYear(vehicle.year);
    const yearFrom = parseYear(row.year_from);
    const yearTo = parseYear(row.year_to);

    if (selectedYear != null) {
      if (yearFrom != null && selectedYear < yearFrom) return false;
      if (yearTo != null && selectedYear > yearTo) return false;
      if (yearFrom == null && yearTo == null) {
        // Row has no year window — don't block on year alone when make/model/series matched.
      }
    }

    return true;
  }

  /**
   * @param {Record<string, string | undefined> | null} vehicle
   * @param {{ makes: string[]; models: string[]; years: string[]; series: string[] }} tagFitment
   */
  function vehicleMatchesTags(vehicle, tagFitment) {
    if (!vehicle?.make) return null;

    const { makes, models, years, series } = tagFitment;
    if (!makes.length && !models.length && !years.length && !series.length) return null;

    const makeMatch =
      !makes.length || makes.some((entry) => normalize(entry) === normalize(vehicle.make));
    const modelMatch =
      !models.length || models.some((entry) => normalize(entry) === normalize(vehicle.model));
    const yearMatch =
      !years.length || years.some((entry) => String(entry) === String(vehicle.year));

    let seriesMatch = true;
    if (series.length && vehicle.series) {
      const seriesValue = normalize(vehicle.series);
      seriesMatch = series.some((entry) => normalize(entry) === seriesValue);
    }

    return makeMatch && modelMatch && yearMatch && seriesMatch;
  }

  /**
   * @param {Record<string, string | undefined> | null} vehicle
   * @param {Record<string, unknown>[]} compatibleVehicles
   */
  function vehicleMatchesMetafield(vehicle, compatibleVehicles) {
    if (!vehicle?.make) return null;
    if (!compatibleVehicles.length) return null;
    return compatibleVehicles.some((row) => vehicleMatchesMetafieldRow(vehicle, row));
  }

  /**
   * @param {Record<string, string | undefined> | null} vehicle
   * @param {{ tags?: string[]; compatibleVehicles?: unknown }} productFitment
   * @returns {boolean | null} null = no fitment data or no vehicle selected
   */
  function matchProductFitment(vehicle, productFitment) {
    if (!vehicle?.make) return null;

    const compatibleVehicles = parseCompatibleVehicles(productFitment?.compatibleVehicles);
    if (compatibleVehicles.length) {
      return vehicleMatchesMetafield(vehicle, compatibleVehicles);
    }

    const tags = Array.isArray(productFitment?.tags) ? productFitment.tags : [];
    return vehicleMatchesTags(vehicle, parseFitmentTags(tags));
  }

  /** @param {Record<string, string | undefined> | null} vehicle */
  function vehicleLabel(vehicle) {
    if (!vehicle) return '';
    return [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ');
  }

  function readSelectedVehicle() {
    try {
      return JSON.parse(window.localStorage.getItem('selectedVehicle') || 'null');
    } catch {
      return null;
    }
  }

  /** @param {string} template @param {string} label */
  function formatCopy(template, label) {
    return template.replace(/\{\{\s*vehicle\s*\}\}/gi, label);
  }

  window.MAPFitment = {
    normalize,
    parseFitmentTags,
    parseCompatibleVehicles,
    matchProductFitment,
    vehicleLabel,
    readSelectedVehicle,
    formatCopy,
  };
})();
